import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Lock, Save, Building2, CreditCard } from "lucide-react";

const Einstellungen = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [usernameForm, setUsernameForm] = useState({
    new_username: "",
    password: "",
  });
  const [bankForm, setBankForm] = useState({
    bank_name: "",
    bank_holder: "",
    bank_iban: "",
    bank_bic: "",
    verwendungszweck: "",
  });
  const [paypalForm, setPaypalForm] = useState({
    paypal_username: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userRes, settingsRes] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/settings`)
      ]);
      setCurrentUser(userRes.data);
      setUsernameForm({ ...usernameForm, new_username: userRes.data.username });
      
      setSettings(settingsRes.data);
      setBankForm({
        bank_name: settingsRes.data.bank_name || "",
        bank_holder: settingsRes.data.bank_holder || "",
        bank_iban: settingsRes.data.bank_iban || "",
        bank_bic: settingsRes.data.bank_bic || "",
        verwendungszweck: settingsRes.data.verwendungszweck || "",
      });
      setPaypalForm({
        paypal_username: settingsRes.data.paypal_username || "",
      });
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error("Neues Passwort muss mindestens 6 Zeichen lang sein");
      return;
    }

    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success("Passwort erfolgreich geändert");
      setPasswordForm({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Ändern des Passworts");
    }
  };

  const handleUsernameChange = async (e) => {
    e.preventDefault();

    if (usernameForm.new_username === currentUser?.username) {
      toast.error("Neuer Benutzername ist identisch mit dem aktuellen");
      return;
    }

    if (usernameForm.new_username.length < 3) {
      toast.error("Benutzername muss mindestens 3 Zeichen lang sein");
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/change-username`, {
        new_username: usernameForm.new_username,
        password: usernameForm.password,
      });
      
      // Update token
      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token);
      }
      
      toast.success("Benutzername erfolgreich geändert");
      setUsernameForm({ ...usernameForm, password: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Ändern des Benutzernamens");
    }
  };

  const handleBankUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/settings`, bankForm);
      toast.success("Bankverbindung erfolgreich gespeichert");
    } catch (error) {
      toast.error("Fehler beim Speichern der Bankverbindung");
    }
  };

  const handlePayPalUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/settings`, paypalForm);
      toast.success("PayPal-Einstellungen erfolgreich gespeichert");
    } catch (error) {
      toast.error("Fehler beim Speichern der PayPal-Einstellungen");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-400">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="einstellungen-container">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Einstellungen</h1>
        <p className="text-gray-600">Verwalten Sie Ihr Admin-Konto und Zahlungsoptionen</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - User Settings */}
        <div className="space-y-6">
          {/* Current User Info */}
          <Card className="card-white">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <User className="w-5 h-5" />
                Aktueller Benutzer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <Label className="text-gray-600">Benutzername</Label>
                  <p className="text-lg font-semibold text-gray-900">{currentUser?.username}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Benutzer-ID</Label>
                  <p className="text-sm text-gray-700 font-mono">{currentUser?.id}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Change Username */}
          <Card className="card-white">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <User className="w-5 h-5" />
                Benutzername ändern
              </CardTitle>
              <CardDescription className="text-gray-600">
                Ändern Sie Ihren Anmelde-Benutzernamen
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUsernameChange} className="space-y-4">
                <div>
                  <Label htmlFor="new_username" className="text-gray-700">Neuer Benutzername</Label>
                  <Input
                    id="new_username"
                    data-testid="new-username-input"
                    type="text"
                    value={usernameForm.new_username}
                    onChange={(e) => setUsernameForm({ ...usernameForm, new_username: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="username_password" className="text-gray-700">Aktuelles Passwort (zur Bestätigung)</Label>
                  <Input
                    id="username_password"
                    data-testid="username-password-input"
                    type="password"
                    value={usernameForm.password}
                    onChange={(e) => setUsernameForm({ ...usernameForm, password: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>
                <Button type="submit" className="btn-primary" data-testid="change-username-button">
                  <Save className="w-4 h-4 mr-2" />
                  Benutzername speichern
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card className="card-white">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Passwort ändern
              </CardTitle>
              <CardDescription className="text-gray-600">
                Ändern Sie Ihr Anmelde-Passwort
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label htmlFor="current_password" className="text-gray-700">Aktuelles Passwort</Label>
                  <Input
                    id="current_password"
                    data-testid="current-password-input"
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="new_password" className="text-gray-700">Neues Passwort</Label>
                  <Input
                    id="new_password"
                    data-testid="new-password-input"
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="confirm_password" className="text-gray-700">Neues Passwort bestätigen</Label>
                  <Input
                    id="confirm_password"
                    data-testid="confirm-password-input"
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>
                <Button type="submit" className="btn-primary" data-testid="change-password-button">
                  <Save className="w-4 h-4 mr-2" />
                  Passwort speichern
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Payment Settings */}
        <div className="space-y-6">
          {/* Bank Details */}
          <Card className="card-white">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Bankverbindung
              </CardTitle>
              <CardDescription className="text-gray-600">
                Wird Abteilungs-Benutzern angezeigt
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBankUpdate} className="space-y-4">
                <div>
                  <Label htmlFor="bank_name" className="text-gray-700">Bank Name</Label>
                  <Input
                    id="bank_name"
                    data-testid="bank-name-input"
                    type="text"
                    value={bankForm.bank_name}
                    onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    placeholder="z.B. Sparkasse Berlin"
                  />
                </div>
                <div>
                  <Label htmlFor="bank_holder" className="text-gray-700">Kontoinhaber</Label>
                  <Input
                    id="bank_holder"
                    data-testid="bank-holder-input"
                    type="text"
                    value={bankForm.bank_holder}
                    onChange={(e) => setBankForm({ ...bankForm, bank_holder: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    placeholder="z.B. Feuerwehr Kantine"
                  />
                </div>
                <div>
                  <Label htmlFor="bank_iban" className="text-gray-700">IBAN</Label>
                  <Input
                    id="bank_iban"
                    data-testid="bank-iban-input"
                    type="text"
                    value={bankForm.bank_iban}
                    onChange={(e) => setBankForm({ ...bankForm, bank_iban: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900 font-mono"
                    placeholder="DE89 3704 0044 0532 0130 00"
                  />
                </div>
                <div>
                  <Label htmlFor="bank_bic" className="text-gray-700">BIC</Label>
                  <Input
                    id="bank_bic"
                    data-testid="bank-bic-input"
                    type="text"
                    value={bankForm.bank_bic}
                    onChange={(e) => setBankForm({ ...bankForm, bank_bic: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900 font-mono"
                    placeholder="COBADEFFXXX"
                  />
                </div>
                <div>
                  <Label htmlFor="verwendungszweck" className="text-gray-700">Verwendungszweck</Label>
                  <Input
                    id="verwendungszweck"
                    data-testid="verwendungszweck-input"
                    type="text"
                    value={bankForm.verwendungszweck}
                    onChange={(e) => setBankForm({ ...bankForm, verwendungszweck: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    placeholder="z.B. Kantinenbeitrag + Wachabteilung"
                  />
                </div>
                <Button type="submit" className="btn-primary w-full" data-testid="save-bank-button">
                  <Save className="w-4 h-4 mr-2" />
                  Bankverbindung speichern
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* PayPal Settings */}
          <Card className="card-white">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                PayPal
              </CardTitle>
              <CardDescription className="text-gray-600">
                PayPal.me Link für Direktzahlungen
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePayPalUpdate} className="space-y-4">
                <div>
                  <Label htmlFor="paypal_username" className="text-gray-700">PayPal Benutzername</Label>
                  <Input
                    id="paypal_username"
                    data-testid="paypal-username-input"
                    type="text"
                    value={paypalForm.paypal_username}
                    onChange={(e) => setPaypalForm({ ...paypalForm, paypal_username: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    placeholder="IhrPayPalName"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Link wird: paypal.me/{paypalForm.paypal_username || "IhrName"}/[Betrag]
                  </p>
                </div>
                <Button type="submit" className="btn-primary w-full" data-testid="save-paypal-button">
                  <Save className="w-4 h-4 mr-2" />
                  PayPal speichern
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Einstellungen;